/**
 * Renders and manages the questions within one V2 survey section, wrapping the V1 QuestionField
 * via a per-section useFieldArray so V1's flat-path FieldList is left untouched.
 */
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '@Models/index';
import {
	FormFieldType,
	ICustomFieldMap,
	ICustomFormFieldTagSettings,
	IFormField,
	ITagGenerationSettings,
} from '@ViewModels';
import { inject } from 'mobx-react';
import { css } from 'aphrodite';
import * as React from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import { ISelectBoxOption } from '@WebComponents/SelectBox';
import { useUserSession } from '../../../../../../../../models/hooks/appStateHooks';
import { useContactCustomFields } from '../../../../../../../../queries/Contact/useContactCustomFields';
import { QuestionField } from '../../../AdditionalQuestions/components/QuestionField';
import type { CustomFieldManagerProps, QuestionFieldProps, TagManagerProps } from '../../../AdditionalQuestions/types';
import {
	QuestionType,
	fieldTypeForQuestionType,
	questionTypeOptions,
} from '../../../FormFieldWithOptions/questionTypes';
import {
	LINEAR_SCALE_DEFAULT_MAX,
	LINEAR_SCALE_DEFAULT_MIN,
	NPS_DEFAULT_END_LABEL,
	NPS_DEFAULT_START_LABEL,
	NPS_MAX,
	NPS_MIN,
	npsQuestionPrompt,
} from '../../../../../../../../surveys/utils/scaleQuestions';
import type { SurveyPart } from '../../types';
import { applyVisibilityCleanup } from '../../utils/validation';
import { isQuestionMappingDirty } from '../../utils/questionMappingDirty';
import { QuestionVisibilityControls } from './QuestionVisibilityControls';
import { ScaleQuestionSettings } from './ScaleQuestionSettings';
import { styleSheet as questionStyleSheet } from './SectionQuestions.styles';

export interface SectionQuestionsProps {
	/** RHF path prefix for this section, e.g. "surveyForm.surveyParts.0" */
	fieldNamePrefix: string;
	sectionIndex: number;
	isEdit: boolean;
	/** Full ordered parts list — used by per-question visibility to enumerate eligible sources. */
	allSurveyParts: SurveyPart[];
}

function hasDirtyLeaf(node: unknown): boolean {
	if (node === true) return true;
	if (node && typeof node === 'object') {
		return Object.values(node).some(hasDirtyLeaf);
	}
	return false;
}

function hasErrorLeaf(node: unknown): boolean {
	if (!node || typeof node !== 'object') return false;
	if (typeof (node as { message?: unknown }).message === 'string') return true;
	return Object.values(node).some(hasErrorLeaf);
}

// Module-level so the reference stays stable (preserves QuestionField/FieldTypeSelector React.memo).
const SECTION_QUESTION_TYPE_OPTIONS: QuestionFieldProps['formFieldOptions'] = questionTypeOptions([
	QuestionType.ShortAnswer,
	QuestionType.DropDown,
	QuestionType.CheckBoxes,
	QuestionType.FileUpload,
	QuestionType.LinearScale,
	QuestionType.NPS,
]);

// Offered to every OTHER question once the survey already has an NPS question (one NPS per survey).
const SECTION_QUESTION_TYPE_OPTIONS_WITHOUT_NPS: QuestionFieldProps['formFieldOptions'] =
	SECTION_QUESTION_TYPE_OPTIONS.filter(option => option.value !== QuestionType.NPS);

const SINGLE_NPS_MESSAGE = 'Only one NPS question is allowed per survey.';

const SCALE_KEYS = ['minValue', 'maxValue', 'startLabel', 'endLabel'] as const;

const defaultScaleConfig = (
	questionType: QuestionType
): { minValue: number; maxValue: number; startLabel: string | null; endLabel: string | null } | null => {
	if (questionType === QuestionType.NPS) {
		return {
			minValue: NPS_MIN,
			maxValue: NPS_MAX,
			startLabel: NPS_DEFAULT_START_LABEL,
			endLabel: NPS_DEFAULT_END_LABEL,
		};
	}
	if (questionType === QuestionType.LinearScale) {
		return { minValue: LINEAR_SCALE_DEFAULT_MIN, maxValue: LINEAR_SCALE_DEFAULT_MAX, startLabel: null, endLabel: null };
	}
	return null;
};

const SectionQuestionsBase: React.FC<SectionQuestionsProps & IImpersonationContextComponentProps> = ({
	fieldNamePrefix,
	sectionIndex,
	isEdit,
	allSurveyParts,
	impersonationContext,
}) => {
	const { control, setValue, formState, getValues } = useFormContext();
	const questionsPath = `${fieldNamePrefix}.questions` as const;

	// Tag/custom-field settings live at root paths shared with V1, keyed by fieldId, so both editors interoperate.
	const tagGenerationSettings = useWatch({ control, name: 'tagGenerationSettings' }) as
		| Partial<ITagGenerationSettings>
		| undefined;
	const customFieldMap = useWatch({ control, name: 'customFieldMap' }) as ICustomFieldMap[] | undefined;

	// Drives the "Link Answer to Custom Field" picker; gated on the account flag (query no-ops otherwise).
	const userSession = useUserSession();
	const hasCustomContactFields = !!userSession?.account?.additionalInfo?.hasCustomContactFields;
	const customFieldsQuery = useContactCustomFields({
		impersonationContext,
		refetchOnWindowFocus: false,
		enabled: hasCustomContactFields,
	});
	const availableCustomFields = customFieldsQuery.data?.fields ?? [];

	// Contact-field ids already claimed by some question (one contact field maps to at most one question).
	const usedCustomFieldIds = React.useMemo(() => (customFieldMap ?? []).map(m => m.contactFieldId), [customFieldMap]);

	// useFieldArray for structural ops only; render from useWatch so in-place setValue edits don't
	// remount QuestionField (which would steal focus).
	const { remove } = useFieldArray({
		control,
		name: questionsPath,
		keyName: '_rhfId',
	});
	const watchedQuestions = (useWatch({ control, name: questionsPath }) ?? []) as IFormField<string>[];

	// One NPS question per survey. Watch ALL parts (across sections) so the guard holds account-wide.
	const allSurveyPartsValues = useWatch({ control, name: 'surveyForm.surveyParts' }) as
		| Array<{ _type?: string; id?: string; questions?: Array<{ _type?: string; id?: string }> }>
		| undefined;
	const existingNpsQuestionId = React.useMemo(() => {
		for (const part of allSurveyPartsValues ?? []) {
			if (part?._type === 'SurveySection') {
				// Require an id so a transient/clone NPS without one doesn't mask a real NPS in the section.
				const npsQuestion = (part.questions ?? []).find(
					question => question?._type === QuestionType.NPS && !!question.id
				);
				if (npsQuestion?.id) {
					return npsQuestion.id;
				}
			} else if (part?._type === QuestionType.NPS && part.id) {
				return part.id;
			}
		}
		return undefined;
	}, [allSurveyPartsValues]);

	// Persisted question ids from defaultValues (tracks the post-load/onSuccess resets); gates the
	// type-selector to disabled for saved questions. Empty in create mode (defaultValues holds the seed).
	const savedQuestionIds = React.useMemo(() => {
		if (!isEdit) return new Set<string>();
		const parts = (
			formState.defaultValues as
				| { surveyForm?: { surveyParts?: Array<{ questions?: Array<{ id?: string }> }> } }
				| undefined
		)?.surveyForm?.surveyParts;
		if (!parts) return new Set<string>();
		const ids = parts
			.flatMap(p => p?.questions ?? [])
			.map(q => q?.id)
			.filter((id): id is string => !!id);
		return new Set(ids);
	}, [isEdit, formState.defaultValues]);

	const { dirtyFields, errors } = formState;

	// Full field replacement via sub-path setValue (preserves _rhfId → no remount/focus loss).
	const onFieldUpdate = React.useCallback(
		(updatedField: IFormField<string>, questionIndex: number) => {
			setValue(`${questionsPath}.${questionIndex}`, updatedField, { shouldDirty: true });
			applyVisibilityCleanup(getValues, setValue);
		},
		[setValue, getValues, questionsPath]
	);

	const onFieldRemove = React.useCallback(
		(questionIndex: number) => {
			remove(questionIndex);
			applyVisibilityCleanup(getValues, setValue);
		},
		[remove, getValues, setValue]
	);

	const onLabelChange = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>, questionIndex: number) => {
			setValue(`${questionsPath}.${questionIndex}.label`, e.target.value, { shouldDirty: true });
		},
		[setValue, questionsPath]
	);

	const onToggleOptional = React.useCallback(
		(questionIndex: number) => {
			const current = getValues(`${questionsPath}.${questionIndex}.isOptional`) as boolean | undefined;
			setValue(`${questionsPath}.${questionIndex}.isOptional`, !current, { shouldDirty: true });
		},
		[setValue, getValues, questionsPath]
	);

	const onFieldTypeChange = React.useCallback(
		(selectedType: ISelectBoxOption<QuestionType>, questionIndex: number) => {
			const questionType = selectedType.value!;
			const ft = fieldTypeForQuestionType(questionType);
			// Captured before overwriting so we can detect a question leaving NPS.
			const previousType = getValues(`${questionsPath}.${questionIndex}._type`) as string | undefined;
			// Defensive guard for one-NPS-per-survey (the dropdown already hides NPS on other questions).
			if (questionType === QuestionType.NPS && existingNpsQuestionId) {
				const thisQuestionId = getValues(`${questionsPath}.${questionIndex}.id`) as string | undefined;
				if (existingNpsQuestionId !== thisQuestionId) {
					toast.error(SINGLE_NPS_MESSAGE);
					return;
				}
			}
			setValue(`${questionsPath}.${questionIndex}._type`, questionType, { shouldDirty: true });
			setValue(`${questionsPath}.${questionIndex}.fieldType`, ft, { shouldDirty: true });
			const scaleConfig = defaultScaleConfig(questionType);
			if (scaleConfig) {
				for (const key of SCALE_KEYS) {
					setValue(`${questionsPath}.${questionIndex}.${key}`, scaleConfig[key], { shouldDirty: true });
				}
			} else {
				for (const key of SCALE_KEYS) {
					setValue(`${questionsPath}.${questionIndex}.${key}`, undefined, { shouldDirty: true });
				}
			}
			// Default the NPS label to the company-name prompt on entry (editable thereafter); on exit
			// clear it only if still the untouched seed, so an author-edited label is never clobbered.
			const npsPromptSeed = npsQuestionPrompt(getValues('options.companyName') as string | undefined);
			if (questionType === QuestionType.NPS) {
				setValue(`${questionsPath}.${questionIndex}.label`, npsPromptSeed, { shouldDirty: true });
			} else if (previousType === QuestionType.NPS) {
				const currentLabel = getValues(`${questionsPath}.${questionIndex}.label`) as string | undefined;
				if (currentLabel === npsPromptSeed) {
					setValue(`${questionsPath}.${questionIndex}.label`, '', { shouldDirty: true });
				}
			}
			applyVisibilityCleanup(getValues, setValue);
			// Prune tag-prefix (Option/MultipleOptions only) and custom-field mappings the new type can't keep.
			const fieldId = getValues(`${questionsPath}.${questionIndex}.id`) as string | undefined;
			if (!fieldId) return;
			if (ft !== FormFieldType.Option && ft !== FormFieldType.MultipleOptions) {
				const existing = tagGenerationSettings?.customFormFieldTagSettings;
				if (existing?.some((s: ICustomFormFieldTagSettings) => s.fieldId === fieldId)) {
					setValue(
						'tagGenerationSettings',
						{
							...tagGenerationSettings,
							customFormFieldTagSettings: existing.filter((s: ICustomFormFieldTagSettings) => s.fieldId !== fieldId),
						},
						{ shouldDirty: true }
					);
				}
			}
			const customFieldSupported =
				ft === FormFieldType.String ||
				ft === FormFieldType.Option ||
				ft === FormFieldType.MultipleOptions ||
				ft === FormFieldType.FileUpload;
			if (!customFieldSupported && customFieldMap?.some(m => m.fieldId === fieldId)) {
				setValue(
					'customFieldMap',
					customFieldMap.filter(m => m.fieldId !== fieldId),
					{ shouldDirty: true }
				);
			}
		},
		[setValue, getValues, questionsPath, tagGenerationSettings, customFieldMap, userSession, existingNpsQuestionId]
	);

	// Upsert a tag-prefix entry; wipes any custom-field mapping on the same fieldId (mutually exclusive).
	const onTagPrefixChange = React.useCallback(
		(fieldId: string, tagPrefix: string) => {
			const current = tagGenerationSettings ?? {
				tagOnCompletion: false,
				completionTag: '',
				customFormFieldTagSettings: [],
			};
			const existing = current.customFormFieldTagSettings ?? [];
			const idx = existing.findIndex((s: ICustomFormFieldTagSettings) => s.fieldId === fieldId);
			const nextEntries = [...existing];
			const entry = { fieldId, shouldGenerateTag: true, tagPrefix };
			if (idx >= 0) nextEntries[idx] = entry;
			else nextEntries.push(entry);
			setValue('tagGenerationSettings', { ...current, customFormFieldTagSettings: nextEntries }, { shouldDirty: true });
			if (customFieldMap?.some(m => m.fieldId === fieldId)) {
				setValue(
					'customFieldMap',
					customFieldMap.filter(m => m.fieldId !== fieldId),
					{ shouldDirty: true }
				);
			}
		},
		[tagGenerationSettings, customFieldMap, setValue]
	);

	const onTagPrefixDelete = React.useCallback(
		(fieldId: string) => {
			if (!tagGenerationSettings?.customFormFieldTagSettings?.length) return;
			setValue(
				'tagGenerationSettings',
				{
					...tagGenerationSettings,
					customFormFieldTagSettings: tagGenerationSettings.customFormFieldTagSettings.filter(
						(s: ICustomFormFieldTagSettings) => s.fieldId !== fieldId
					),
				},
				{ shouldDirty: true }
			);
		},
		[tagGenerationSettings, setValue]
	);

	// Upsert a custom-field mapping; `contactFieldId` holds the field NAME (BE contract). Mutually
	// exclusive with tag-prefix on the same fieldId.
	const onCustomFieldChange = React.useCallback(
		(fieldId: string, _customFieldId: string, customFieldName: string) => {
			const current = customFieldMap ?? [];
			const idx = current.findIndex(m => m.fieldId === fieldId);
			const next = [...current];
			const entry: ICustomFieldMap = { fieldId, contactFieldId: customFieldName };
			if (idx >= 0) next[idx] = entry;
			else next.push(entry);
			setValue('customFieldMap', next, { shouldDirty: true });
			if (tagGenerationSettings?.customFormFieldTagSettings?.some(s => s.fieldId === fieldId)) {
				setValue(
					'tagGenerationSettings',
					{
						...tagGenerationSettings,
						customFormFieldTagSettings: tagGenerationSettings.customFormFieldTagSettings.filter(
							(s: ICustomFormFieldTagSettings) => s.fieldId !== fieldId
						),
					},
					{ shouldDirty: true }
				);
			}
		},
		[customFieldMap, tagGenerationSettings, setValue]
	);

	const onCustomFieldDelete = React.useCallback(
		(fieldId: string) => {
			if (!customFieldMap?.length) return;
			setValue(
				'customFieldMap',
				customFieldMap.filter(m => m.fieldId !== fieldId),
				{ shouldDirty: true }
			);
		},
		[customFieldMap, setValue]
	);

	const sectionDirtyFields =
		(
			dirtyFields as Record<string, unknown> & {
				surveyForm?: { surveyParts?: Array<{ questions?: unknown[] }> };
			}
		)?.surveyForm?.surveyParts?.[sectionIndex]?.questions ?? [];

	const sectionErrors =
		(
			errors as Record<string, unknown> & {
				surveyForm?: { surveyParts?: Array<{ questions?: unknown[] }> };
			}
		)?.surveyForm?.surveyParts?.[sectionIndex]?.questions ?? [];

	// Tag/custom-field mappings live in root arrays (not the question subtree), so hasDirtyLeaf misses
	// them; treat a question as dirty when its mapping differs from defaultValues (mirrors V1).
	const defaultTagSettings =
		(formState.defaultValues as { tagGenerationSettings?: Partial<ITagGenerationSettings> } | undefined)
			?.tagGenerationSettings?.customFormFieldTagSettings ?? [];
	const defaultCustomFieldMap =
		(formState.defaultValues as { customFieldMap?: ICustomFieldMap[] } | undefined)?.customFieldMap ?? [];

	const mappingDirtyForField = (fieldId?: string): boolean =>
		isQuestionMappingDirty({
			fieldId,
			currentTagSettings: tagGenerationSettings?.customFormFieldTagSettings,
			defaultTagSettings,
			currentCustomFieldMap: customFieldMap,
			defaultCustomFieldMap,
		});

	return (
		<>
			{watchedQuestions.map((field, questionIndex) => {
				const questionDirtyNode = (sectionDirtyFields as unknown[])[questionIndex];
				const questionErrorNode = (sectionErrors as unknown[])[questionIndex];

				const isDirty = hasDirtyLeaf(questionDirtyNode) || mappingDirtyForField(field.id);
				const hasBeenSaved = Boolean(field.id && savedQuestionIds.has(field.id));
				const hasError = hasErrorLeaf(questionErrorNode);

				// Hide NPS for every OTHER question once the survey has one; the NPS question keeps it selectable.
				const npsClaimedByAnotherQuestion = !!existingNpsQuestionId && existingNpsQuestionId !== field.id;
				const questionTypeOptionsForField = npsClaimedByAnotherQuestion
					? SECTION_QUESTION_TYPE_OPTIONS_WITHOUT_NPS
					: SECTION_QUESTION_TYPE_OPTIONS;
				const questionErrors = questionErrorNode as
					| { label?: { message?: string }; options?: { message?: string } }
					| undefined;

				const isOptionField =
					field.fieldType === FormFieldType.MultipleOptions || field.fieldType === FormFieldType.Option;
				const tagManagerProps: TagManagerProps | undefined = isOptionField
					? {
							fieldId: field.id,
							fieldLabel: field.label,
							isOptionField: true,
							questionIndex,
							tagGenerationSettings,
							onTagPrefixChange,
							onTagPrefixDelete,
							disabled: false,
						}
					: undefined;

				// Filter this question's own mapping out of usedCustomFieldIds so it can re-pick its target.
				const isSupportedCustomFieldType =
					field.fieldType === FormFieldType.String ||
					field.fieldType === FormFieldType.Option ||
					field.fieldType === FormFieldType.MultipleOptions ||
					field.fieldType === FormFieldType.FileUpload;
				const currentMapping = customFieldMap?.find(m => m.fieldId === field.id);
				const filteredUsedCustomFieldIds = usedCustomFieldIds.filter(id => id !== currentMapping?.contactFieldId);
				const customFieldManagerProps: CustomFieldManagerProps | undefined =
					hasCustomContactFields && isSupportedCustomFieldType
						? {
								fieldId: field.id,
								fieldLabel: field.label,
								isOptionField,
								customFieldMap,
								onCustomFieldChange,
								onCustomFieldDelete,
								disabled: false,
								availableCustomFields,
								usedCustomFieldIds: filteredUsedCustomFieldIds,
								questionIndex,
							}
						: undefined;

				return (
					<div key={field.id} className={css(questionStyleSheet.questionContainer)}>
						<QuestionField
							field={field}
							questionIndex={questionIndex}
							isEdit={isEdit}
							isDirty={isDirty}
							hasBeenSaved={hasBeenSaved}
							hasError={hasError}
							formFieldOptions={questionTypeOptionsForField}
							tagManagerProps={tagManagerProps}
							customFieldManagerProps={customFieldManagerProps}
							onFieldUpdate={onFieldUpdate}
							onFieldRemove={watchedQuestions.length > 1 ? onFieldRemove : undefined}
							onLabelChange={onLabelChange}
							onToggleOptional={onToggleOptional}
							onFieldTypeChange={onFieldTypeChange}
							questionErrors={questionErrors}
						>
							<ScaleQuestionSettings questionFieldNamePrefix={`${questionsPath}.${questionIndex}`} />
							<QuestionVisibilityControls
								questionFieldNamePrefix={`${questionsPath}.${questionIndex}`}
								sectionIndex={sectionIndex}
								questionIndex={questionIndex}
								allSurveyParts={allSurveyParts}
							/>
						</QuestionField>
					</div>
				);
			})}
		</>
	);
};

SectionQuestionsBase.displayName = 'SectionQuestionsBase';

// inject(ImpersonationContextKey) supplies impersonationContext for useContactCustomFields (admin impersonation).
export const SectionQuestions = inject(ImpersonationContextKey)(
	SectionQuestionsBase
) as React.FC<SectionQuestionsProps>;
